import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Tabs, Tab, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';

const promoSlides = [
  { title: 'Buy Prompts', subtitle: 'High-converting prompts for creators', bg: '#e3f2fd' },
  { title: 'Buy Ebooks', subtitle: 'Step-by-step guides to grow online', bg: '#f3e5f5' },
  { title: 'Buy Reels Bundle', subtitle: 'Hooks and content plans for IG growth', bg: '#e8f5e9' },
];

const Products = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('popular');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/products.json', { cache: 'no-store' });
        const data = await res.json();
        setCategories(data.categories || []);
        setItems(data.items || []);
        setActive((data.categories && data.categories[0]?.key) || '');
      } catch (e) {
        setError('Failed to load products.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: true,
  };

  const filteredSorted = useMemo(() => {
    let list = items;
    if (active) list = list.filter((i) => i.category === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (i) => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q)
      );
    }
    if (sort === 'price_asc') list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === 'price_desc') list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === 'title') list = [...list].sort((a, b) => a.title.localeCompare(b.title));
    return list;
  }, [items, active, query, sort]);

  const renderBadgeRow = (badges) => (
    badges && badges.length ? (
      <div className="mb-2">
        {badges.map((b, i) => (
          <span key={i} className="badge bg-warning text-dark me-2">{b}</span>
        ))}
      </div>
    ) : null
  );

  const renderGrid = (list) => (
    <Row className="g-4">
      {list.map((p) => (
        <Col key={p.id} md={4} sm={6} xs={12}>
          <Card className="h-100 shadow-sm">
            <Card.Img variant="top" src={p.image} alt={p.title} />
            <Card.Body>
              {renderBadgeRow(p.badges)}
              <Card.Title>{p.title}</Card.Title>
              <Card.Text className="text-muted">{p.description}</Card.Text>
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between align-items-center">
              <div className="fw-bold">₹{p.price}</div>
              <Button variant="primary" onClick={() => navigate(`/products/${p.id}`)}>
                View Details
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      ))}
    </Row>
  );

  return (
    <div className="products-page">
      <Container className="mb-5">
        <section className="mb-4">
          <Slider {...sliderSettings}>
            {promoSlides.map((s, idx) => (
              <div key={idx}>
                <div
                  style={{
                    background: s.bg,
                    borderRadius: 12,
                    padding: '40px 24px',
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  className="px-3"
                >
                  <div>
                    <h3 className="mb-2">{s.title}</h3>
                    <p className="lead mb-0">{s.subtitle}</p>
                  </div>
                  <div className="d-none d-md-block">
                    <i className="bi bi-bag-check-fill fs-1 text-primary"></i>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </section>

        <section className="mb-3">
          <Row className="g-2 align-items-center">
            <Col md={6}>
              <Form.Control
                type="search"
                placeholder="Search products..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="popular">Sort: Popular</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="title">Title A-Z</option>
              </Form.Select>
            </Col>
          </Row>
        </section>

        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div className="text-center py-5">Loading...</div>
        ) : (
          <section>
            <Tabs activeKey={active} onSelect={(k) => setActive(k || '')} id="products-tabs" className="mb-3">
              {categories.map((c) => (
                <Tab key={c.key} eventKey={c.key} title={c.label}>
                  {renderGrid(filteredSorted.filter((i) => i.category === c.key))}
                </Tab>
              ))}
            </Tabs>
          </section>
        )}
      </Container>
    </div>
  );
};

export default Products;
